import { routeFactory } from "@/RouteFactory";
import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import { FppSearchFilter } from "@/features/search-filter/components/FppSearchFilter";
import {
  SearchFilterSelectorContext,
  SearchFilterSelectorContextType,
} from "@/features/search-filter/context/searchFilterSelectorContext";
import { createFppSearchFilterActions } from "@/features/search-filter/redux/reducer";
import {
  StatisticsContext,
  StatisticsContextType,
} from "@/features/statistics/StatisticsContext";
import { createResultTableConfig } from "@/features/statistics/config/ResultTableConfig";
import { useStatisticsYearSelect } from "@/features/statistics/hooks/UseStatisticsYearSelector";
import { ResultTable } from "@/features/statistics/components/ResultTable";
import { getNegotiationStatusCodes } from "@/features/statistics/lib/transformers/NegotiationStatusFormModelTransformer";
import { Box, Row, SeparatorLine } from "@stenajs-webui/core";
import { Card, CardBody, CardHeader } from "@stenajs-webui/elements";
import { SearchFilterContext } from "@stenajs-webui/filter";
import { useContext, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { statisticsBySellerFilterSettings } from "../config/StatisticsBySellerFilterConfig";
import { useRouteListVolumeAndTargetQuery } from "../hooks/UseRouteListVolumeAndTargetQuery";
import { useSellerListQuery } from "../hooks/UseSellerListQuery";
import { transformResultToTableRow } from "../lib/transformers/seller.transformer";

export const StatisticsBySellerTable: React.FC = () => {
  const sticky = true;
  const navigate = useNavigate();

  const {
    sellerCustomerOrVolumeFilter,
    setSellerCustomerOrVolumeFilter,
    sellerSelectedStatuses,
    setSellerSelectedStatuses,
  } = useContext(StatisticsContext) as StatisticsContextType;

  const dispatch = useDispatch();

  const { year: negotiationYear } = useStatisticsYearSelect();
  const { currency: currencyCode } = useCurrencySelection();

  const { committedState, workspaceState } = useContext(
    SearchFilterSelectorContext
  ) as SearchFilterSelectorContextType;
  const { sellers } = committedState;
  const { loading, sellerItems, fetchSellerList } = useSellerListQuery();
  const negotiationStatusCodes = useMemo(
    () => getNegotiationStatusCodes(sellerSelectedStatuses),
    [sellerSelectedStatuses]
  );

  const onSearch = () => {
    const { sellers } = workspaceState.formModel;
    sellers.length > 0 &&
      fetchSellerList({
        variables: {
          filterOptions: {
            sellerIds: sellers.map((seller) => seller.id),
            year: negotiationYear,
          },
        },
      });
  };

  const { loading: loadingVolumeAndTarget, volumeAndTargets } =
    useRouteListVolumeAndTargetQuery({
      year: negotiationYear,
      sellerIds: sellers.map((u) => u.id),
      negotiationStatusCodes,
      currencyCode,
    });

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiationYear]);

  const resultRows = sellerItems?.map((s) =>
    transformResultToTableRow(
      s,
      loadingVolumeAndTarget ? [] : volumeAndTargets ?? [],
      sellerCustomerOrVolumeFilter
    )
  );

  const resultConfig = createResultTableConfig(
    "Seller",
    (id) => navigate(routeFactory.statistics.sellerDetails(id)),
    currencyCode,
    sellerSelectedStatuses,
    setSellerSelectedStatuses,
    sellerCustomerOrVolumeFilter,
    setSellerCustomerOrVolumeFilter,
    sticky
  );

  return (
    <Card minWidth={"900px"}>
      <CardHeader text={"Seller"} />
      <CardBody>
        <Row>
          <Box>
            <SearchFilterContext
              state={workspaceState}
              actions={createFppSearchFilterActions(
                "statistics.statisticsBySeller"
              )}
              dispatch={dispatch}
            >
              <FppSearchFilter
                settings={statisticsBySellerFilterSettings}
                onSearch={onSearch}
              />
            </SearchFilterContext>
          </Box>
        </Row>
      </CardBody>
      <SeparatorLine />
      <ResultTable items={resultRows} config={resultConfig} loading={loading} />
    </Card>
  );
};
